import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { useParams } from "react-router-dom";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { api } from "@/redux/api";
import {
	addEmailData,
	applyCrmProspectUpdate,
	setListPersonAsyncStatus,
	updateOrganizationContact,
	upsertListPersonColumnValue,
} from "@/pages/Private/redux/list/list.slice";
import { AsyncProcessingStatus, AsyncProcessingType } from "@/enum/list.enum";
import {
	addContactEmailData,
	applyContactCrmProspectUpdate,
	updateContactOrganizationContact,
} from "@/pages/Private/redux/contact/contact.slice";
import { CrmProspectUpdateSchema } from "@/pages/Private/redux/crm/crm.schema";
import { RunPromptColumnValueUpdateSchema } from "@/pages/Private/pages/List/schema/columnValue";
import {
	applySearchContactEmailData,
	updateEnrichPersonContact,
} from "@/pages/Private/redux/search/search.slice";
import { GlobalSearchContactUpdateSchema } from "@/pages/Private/redux/search/search.schema";

interface SocketContextType {
	socket: Socket | null;
	actualData: EmailDataUpdate[];
	clearActualData: () => void;
}

export interface EmailDataUpdate {
	prospectId: number;
	email: string;
	status: string;
}

export interface CompanyContactUpdate {
	organizationId: number;
	email: string | null;
	phone: string | null;
	companyId: number;
	listPersonIds: number[];
}

export enum MessageSocketAction {
	JOIN_COMPANY = "join-company",
	ALL_DATA = "all-data",
	COMPANY_CONTACT_UPDATED = "company-contact-updated",
	CRM_PROSPECT_UPDATED = "crm-prospect-updated",
	RUN_PROMPT_UPDATED = "run-prompt-updated",
	RUN_PROMPT_COLUMN_VALUE_UPDATED = "run-prompt-column-value-updated",
	GLOBAL_SEARCH_CONTACT_FOUND = "global-search-contact-found",
	BULK_FIND_PERSON_UPDATED = "bulk-find-person-updated",
}

export interface BulkFindPersonStatusUpdate {
	companyId: number;
	listId: number;
	listPersonId: number;
	asyncProcessingType: AsyncProcessingType;
	asyncProcessingStatus: AsyncProcessingStatus;
	matched?: boolean;
	prospectId?: number;
	error?: string;
}

export interface RunPromptStatusUpdate {
	companyId: number;
	listId: number;
	listPersonId: number;
	asyncProcessingType: AsyncProcessingType;
	asyncProcessingStatus: AsyncProcessingStatus;
	error?: string;
}

const MessageSocketContext = createContext<SocketContextType | undefined>(undefined);

export const MessageSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [actualData, setActualData] = useState<EmailDataUpdate[]>([]);
	const dispatch = useDispatch();
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;

	const clearActualData = () => {
		setActualData([]);
	};

	useEffect(() => {
		if (!token || !auth.user?.company?.id) {
			return;
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;
		let findPersonRefetchTimer: ReturnType<typeof setTimeout> | null = null;

		newSocket.on("connect", () => {
			newSocket.emit(MessageSocketAction.JOIN_COMPANY, auth.user?.company?.id);
			hasLoggedError = false;
			console.info("Socket connected");
		});

		newSocket.on("connect_error", (error) => {
			if (!hasLoggedError) {
				console.info("Socket connection error:", error);
				hasLoggedError = true;
			}
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("Socket disconnected:", reason);
		});

		setSocket(newSocket);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		newSocket.on(MessageSocketAction.ALL_DATA, (data: any) => {
			const payload = {
				prospectId: data.prospectId,
				email: data?.email || "",
				emailStatus: data?.emailStatus || "",
				phone: data?.phone || "",
				phoneStatus: data?.phoneStatus || "",
			};

			dispatch(addEmailData(payload));
			dispatch(addContactEmailData(payload));
			dispatch(applySearchContactEmailData(payload));
		});

		newSocket.on(MessageSocketAction.COMPANY_CONTACT_UPDATED, (data: CompanyContactUpdate) => {
			dispatch(
				updateOrganizationContact({
					organizationId: data.organizationId,
					email: data.email,
					phone: data.phone,
					listPersonIds: data.listPersonIds,
				})
			);
			dispatch(
				updateContactOrganizationContact({
					organizationId: data.organizationId,
					email: data.email,
					phone: data.phone,
				})
			);
		});

		newSocket.on(MessageSocketAction.CRM_PROSPECT_UPDATED, (data: unknown) => {
			const result = CrmProspectUpdateSchema.safeParse(data);

			if (!result.success || result.data.companyId !== auth.user?.company?.id) {
				return;
			}

			dispatch(applyCrmProspectUpdate(result.data));
			dispatch(applyContactCrmProspectUpdate(result.data));
		});

		newSocket.on(MessageSocketAction.RUN_PROMPT_UPDATED, (data: RunPromptStatusUpdate) => {
			if (data.companyId !== auth.user?.company?.id) {
				return;
			}

			dispatch(
				setListPersonAsyncStatus({
					listPersonId: data.listPersonId,
					asyncProcessingType: data.asyncProcessingType,
					asyncProcessingStatus: data.asyncProcessingStatus,
				})
			);
		});

		newSocket.on(MessageSocketAction.RUN_PROMPT_COLUMN_VALUE_UPDATED, (data: unknown) => {
			const result = RunPromptColumnValueUpdateSchema.safeParse(data);

			console.log(data, result);

			if (!result.success || result.data.companyId !== auth.user?.company?.id) {
				return;
			}

			dispatch(
				upsertListPersonColumnValue({
					listPersonId: result.data.listPersonId,
					columnValue: result.data.columnValue,
				})
			);
		});

		newSocket.on(MessageSocketAction.GLOBAL_SEARCH_CONTACT_FOUND, (data: unknown) => {
			const result = GlobalSearchContactUpdateSchema.safeParse(data);

			if (!result.success || result.data.companyId !== auth.user?.company?.id) {
				return;
			}

			dispatch(updateEnrichPersonContact(result.data));
		});

		// Bulk find-person is async and can touch hundreds of rows: reflect each row's status live for
		// the spinner, and coalesce the many terminal events into a single debounced ListPerson refetch
		// so matched (now ADDED) rows drop out of the active "Not found" filter while unmatched rows stay.
		newSocket.on(
			MessageSocketAction.BULK_FIND_PERSON_UPDATED,
			(data: BulkFindPersonStatusUpdate) => {
				if (data.companyId !== auth.user?.company?.id) {
					return;
				}

				dispatch(
					setListPersonAsyncStatus({
						listPersonId: data.listPersonId,
						asyncProcessingType: data.asyncProcessingType,
						asyncProcessingStatus: data.asyncProcessingStatus,
					})
				);

				const isTerminal =
					data.asyncProcessingStatus === AsyncProcessingStatus.DONE ||
					data.asyncProcessingStatus === AsyncProcessingStatus.FAILED;

				if (isTerminal) {
					if (findPersonRefetchTimer) {
						clearTimeout(findPersonRefetchTimer);
					}

					findPersonRefetchTimer = setTimeout(() => {
						dispatch(api.util.invalidateTags(["ListPerson"]));
					}, 1500);
				}
			}
		);

		return () => {
			if (findPersonRefetchTimer) {
				clearTimeout(findPersonRefetchTimer);
			}

			newSocket.disconnect();
		};
	}, [token, auth.user?.company?.id]);

	return (
		<MessageSocketContext.Provider
			value={{
				socket,
				actualData,
				clearActualData,
			}}
		>
			{children}
		</MessageSocketContext.Provider>
	);
};

export const useMessageSocket = () => {
	const context = useContext(MessageSocketContext);

	if (!context) {
		throw new Error("useMessageSocket must be used within a MessageSocketProvider");
	}

	return context;
};
